import React, { useState } from 'react';
import {
  List,
  Datagrid,
  TextField,
  Pagination,
  FunctionField,
  useDataProvider,
  useRefresh,
  useGetIdentity,
  usePermissions,
} from 'react-admin';
import { toast } from 'react-toastify';
import { DEFAULTS } from '../../constants';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import EditDivisionDialog from './EditDivisionDialog';
import { DivisionFormValues } from '../../utils/DataTypes';

const formatDate = (value: any) => {
  if (!value) return '';
  const timestamp = typeof value === 'string' ? parseInt(value, 10) : value;
  const date = new Date(timestamp);
  return isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
};

const DivisionsTable = () => {
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();
  const { permissions } = usePermissions();

  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [recordToDelete, setRecordToDelete] = useState<any | null>(null);

  const initialFormState: DivisionFormValues = {
    id: '',
    billing_client_number: '',
    billing_client_name: '',
    division_number: '',
    division_name: '',
    division_display_number: '',
  };

  const [formValues, setFormValues] = useState(initialFormState);
  const [originalValues, setOriginalValues] = useState(initialFormState);

  const handleEdit = (record: any) => {
    setEditingRowId(record.client_divisions_key_id);

    const initialValues = {
      id: record.id,
      billing_client_number: record.billing_client_number || '',
      billing_client_name: record.billing_client_name || '',
      division_number: record.division_number || '',
      division_name: record.division_name || '',
      division_display_number: record.division_display_number || '',
    };

    setFormValues(initialValues);
    setOriginalValues(initialValues);
    setIsDialogOpen(true);
  };

  const getChangedFields = () => {
    const changedFields: Record<string, any> = {};

    Object.keys(formValues).forEach((key) => {
      const rawCurrent = formValues[key as keyof typeof formValues];
      const rawOriginal = originalValues[key as keyof typeof originalValues];

      const cleanCurrent = typeof rawCurrent === 'string' ? rawCurrent.trim() : rawCurrent;
      const cleanOriginal = typeof rawOriginal === 'string' ? rawOriginal.trim() : rawOriginal;

      if (cleanCurrent !== cleanOriginal) {
        changedFields[key] = cleanCurrent === null ? '' : cleanCurrent;
      }
    });

    return changedFields;
  };

  const handleSave = async () => {
    const rawChangedFields = getChangedFields();

    const trimmedChangedFields: Record<string, any> = {};
    Object.keys(rawChangedFields).forEach((key) => {
      const value = rawChangedFields[key];
      trimmedChangedFields[key] = typeof value === 'string' ? value.trim() : value;
    });

    if (Object.keys(trimmedChangedFields).length === 0) {
      toast.info('No changes made.');
      handleDialogClose();
      return;
    }

    const payload = {
      client_divisions_key_id: editingRowId,
      last_modified_by_text: identity?.fullName || DEFAULTS.UNKNOWN_USER,
      ...trimmedChangedFields,
    };

    try {
      await dataProvider.update('DivisionsCrosswalk', {
        id: editingRowId,
        data: payload,
        previousData: originalValues,
      });
      toast.success('Division Crosswalk updated successfully!');
      refresh();
    } catch (error) {
      toast.error('Update failed.');
    }
    handleDialogClose();
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingRowId(null);
  };

  const handleDeleteClick = (record: any) => {
    setRecordToDelete(record);
  };

  const handleDeleteConfirm = async () => {
    if (!recordToDelete) return;
    try {
      await dataProvider.delete('SoftDeleteDivision', {
        id: recordToDelete.client_divisions_key_id,
        previousData: {
          id: recordToDelete.client_divisions_key_id,
          user: identity?.fullName || DEFAULTS.UNKNOWN_USER,
        },
      });

      toast.success('Division deleted successfully');
      refresh();
    } catch (error: any) {
      toast.error('Delete failed');
    }
    setRecordToDelete(null);
  };
  return (
    <>
      <List
        resource="DivisionsCrosswalk"
        actions={false}
        pagination={<Pagination />}
        sort={{ field: 'billing_client_number', order: 'ASC' }}
        sx={{ width: '100%', '& .RaList-content': { boxShadow: 'none' } }}
      >
        <Datagrid
          bulkActionButtons={false}
          sx={{
            tableLayout: 'fixed',
            '& .MuiTableCell-head': {
              textAlign: 'center',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            },
            '& .MuiTableCell-body': {
              textAlign: 'center',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            },
          }}
        >
          <TextField source="billing_client_name" label="Billing Client Name" sortable={true} />
          <TextField source="billing_client_number" label="Billing Client Number" sortable={true} />
          <TextField source="division_number" label="Division Number" sortable={true} />
          <TextField source="division_display_number" label="Display Number" sortable={true} />
          <TextField source="division_name" label="Division Name" sortable={true} />
          <FunctionField
            key="created_ts"
            label="Created On"
            render={(record: any) => formatDate(record.created_ts)}
            sortable={false}
          />
          <TextField source="created_by_text" label="Created By" />
          <FunctionField
            key="last_modified_ts"
            label="Last Modified On"
            render={(record: any) => formatDate(record.last_modified_ts)}
            sortable={false}
          />
          <TextField source="last_modified_by_text" label="Last Modified By" sortable={false} />
          {permissions.adminPermission && (
            <FunctionField
              label="Actions"
              render={(record: any) => (
                <Box display="flex" justifyContent="center" gap={1}>
                  <button
                    onClick={() => handleEdit(record)}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
                  >
                    <EditIcon style={{ color: '#00909A' }} />
                  </button>

                  <button
                    onClick={() => handleDeleteClick(record)}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
                  >
                    <DeleteIcon style={{ color: '#00909A' }} />
                  </button>
                </Box>
              )}
              sortable={false}
            />
          )}
        </Datagrid>
        {isDialogOpen && (
          <EditDivisionDialog
            isOpen={isDialogOpen}
            formValues={formValues}
            originalValues={originalValues}
            setFormValues={setFormValues}
            onClose={handleDialogClose}
            onSave={handleSave}
          />
        )}
      </List>
      <Dialog open={!!recordToDelete} onClose={() => setRecordToDelete(null)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{recordToDelete?.division_name || 'this division'}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecordToDelete(null)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="primary" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DivisionsTable;
